import { useState } from 'react'
import { Menu, X, Newspaper } from 'lucide-react'
import { MainNav } from './MainNav'
import { UserMenu } from './UserMenu'

export interface NavigationItem {
  label: string
  href: string
  icon?: React.ReactNode
  isActive?: boolean
  badge?: number | boolean
}

export interface AppShellProps {
  children: React.ReactNode
  navigationItems: NavigationItem[]
  user?: {
    name: string
    email?: string
    avatarUrl?: string
    admin?: boolean
  }
  onNavigate?: (href: string) => void
  onUserSettings?: () => void
  onSettings?: () => void
  onGemTemplates?: () => void
  onLogout?: () => void
}

export function AppShell({
  children,
  navigationItems,
  user,
  onNavigate,
  onUserSettings,
  onSettings,
  onGemTemplates,
  onLogout,
}: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-60 bg-stone-50 dark:bg-stone-900
          border-r border-stone-200 dark:border-stone-800
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo / Brand */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-stone-200 dark:border-stone-800">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
                <Newspaper className="w-4.5 h-4.5 text-white" />
              </div>
              <span className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                Inbox Summaries
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 rounded-md lg:hidden text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Navigation */}
          <MainNav
            items={navigationItems}
            onNavigate={(href) => {
              onNavigate?.(href)
              setSidebarOpen(false)
            }}
          />

          {/* Bottom Section */}
          {user && (
            <div className="mt-auto border-t border-stone-200 dark:border-stone-800">
              <UserMenu
                user={user}
                onUserSettings={onUserSettings}
                onSettings={onSettings}
                onGemTemplates={onGemTemplates}
                onLogout={onLogout}
              />
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-60">
        {/* Mobile Header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-md text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5 ml-3">
            <div className="w-7 h-7 rounded-lg bg-teal-600 flex items-center justify-center">
              <Newspaper className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-semibold text-stone-900 dark:text-stone-100">
              Inbox Summaries
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
          {children}
        </main>
      </div>
    </div>
  )
}
